import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { X, Shield, User } from 'lucide-react';
import { impersonationService } from '@/services/impersonation';
import { appPath } from '@/lib/basePath';
import { getCurrentAdminSurfaceContext } from '@/lib/paths';

export default function ImpersonationBanner() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [tenant, setTenant] = useState<{ id: string; name: string; slug: string } | null>(null);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = impersonationService.isImpersonating();
      setIsImpersonating(impersonating);
      
      if (impersonating) {
        const tenantData = impersonationService.getImpersonatedTenant();
        setTenant(tenantData);
      }
    };

    checkImpersonation();
    
    const interval = setInterval(checkImpersonation, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleExitImpersonation = async () => {
    try {
      setExiting(true);
      const response = await impersonationService.exitImpersonate();
      
      if (response.success) {
        window.location.href = appPath(getCurrentAdminSurfaceContext().homePath);
      } else {
        console.error('Failed to exit impersonation');
        setExiting(false);
      }
    } catch (err) {
      console.error('Failed to exit impersonation:', err);
      setExiting(false);
    }
  };

  if (!isImpersonating || !tenant) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <Alert className="border-yellow-200 bg-yellow-50 text-yellow-900 rounded-none shadow-md">
        <Shield className="h-4 w-4 text-yellow-700" />
        <AlertDescription className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-yellow-700" />
              <span className="font-semibold">
                Impersonating: {tenant.name}
              </span>
              <span className="text-sm text-yellow-700">
                ({tenant.slug})
              </span>
            </div>
            <div className="text-xs text-yellow-600">
              You are viewing this tenant's account as master administrator
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExitImpersonation}
            disabled={exiting}
            className="border-yellow-600 text-yellow-900 hover:bg-yellow-100"
          >
            <X className="h-4 w-4 mr-2" />
            {exiting ? 'Exiting...' : 'Exit Impersonation'}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
